import { useEffect, useState } from 'react';
import {
  useRefreshAccountTreatmentIndicators,
  useCreateAccountTreatmentIndicator,
  useUpdateAccountTreatmentIndicator,
  useAccountFlagDefinitions,
} from '@app/sdk';
import { ComponentErrorBoundary } from '@app/utilities';
import { FormProvider, useForm } from 'react-hook-form';
import { FormContainer, Input, Select, InputProps, SelectProps, toSelectOption, SelectOption } from '@app/shared';

type AccountTreatmentIndicatorModel = {
  id: string;
  name: string;
  behaviourSettings: BehaviourSettingsModel;
  accountFlag: string;
};

type BehaviourSettingsModel = {
  acceptAuths: string;
  acceptClearing: string;
  accrueInterest: string;
  chargeInterest: string;
  chargeFees: string;
  generateStatements: string;
  allowRegularPaymentCollection: string;
  pauseBilling: string;
  allowStatementDayChange: string;
  enableNewDuesAndAging: string;
  enablePaymentDueDateNosiaCheck: string;
  enableRetrospectiveNosiaCheck: string;
  enableAutomaticChargeOff: string;
  generateAnnualStatement: string;
  enablePersistentDebtCalculation: string;
};

type AccountTreatmentIndicatorFormModel = {
  id: string;
  name: string;
  accountFlag: string;
  acceptAuths: string;
  acceptClearing: string;
  accrueInterest: string;
  chargeInterest: string;
  chargeFees: string;
  generateStatements: string;
  allowRegularPaymentCollection: string;
  pauseBilling: string;
  allowStatementDayChange: string;
  enableNewDuesAndAging: string;
  enablePaymentDueDateNosiaCheck: string;
  enableRetrospectiveNosiaCheck: string;
  enableAutomaticChargeOff: string;
  generateAnnualStatement: string;
  enablePersistentDebtCalculation: string;
};

export function AccountTreatmentIndicatorForm({
  accountTreatmentIndicator,
}: {
  accountTreatmentIndicator: AccountTreatmentIndicatorModel;
}) {
  const { mutateAsync: create, isPending: isCreating } = useCreateAccountTreatmentIndicator();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateAccountTreatmentIndicator();
  const refreshAccountTreatmentIndicators = useRefreshAccountTreatmentIndicators();
  const { data: accountFlagDefinitions } = useAccountFlagDefinitions();
  const [treatmentFlags, setTreatmentFlags] = useState<SelectOption[]>([]);

  useEffect(() => {
    const mappedData = accountFlagDefinitions
      ?.filter((flag) => flag.usage.toLocaleLowerCase() == 'treatment' || flag.usage.toLocaleLowerCase() == 'all')
      .map((flag) => {
        return {
          label: flag.key,
          value: flag.key,
          tooltip: flag.description,
        };
      });
    if (accountFlagDefinitions) {
      setTreatmentFlags(mappedData);
    }
  }, [accountFlagDefinitions]);

  async function onSaveTreatment(data: AccountTreatmentIndicatorFormModel) {
    if (accountTreatmentIndicator?.id) {
      const request = {
        ...accountTreatmentIndicator,
        ...data,
      };
      await update({ id: accountTreatmentIndicator.id, request });
    } else {
      const request = {
        accountTreatmentIndicators: [
          {
            name: data.name,
            accountFlag: data.accountFlag,
            behaviourSettings: {
              acceptAuths: data.acceptAuths,
              acceptClearing: data.acceptClearing,
              accrueInterest: data.accrueInterest,
              chargeInterest: data.chargeInterest,
              chargeFees: data.chargeFees,
              generateStatements: data.generateStatements,
              allowRegularPaymentCollection: data.allowRegularPaymentCollection,
              pauseBilling: data.pauseBilling,
              allowStatementDayChange: data.allowStatementDayChange,
              enableNewDuesAndAging: data.enableNewDuesAndAging,
              enablePaymentDueDateNosiaCheck: data.enablePaymentDueDateNosiaCheck,
              enableRetrospectiveNosiaCheck: data.enableRetrospectiveNosiaCheck,
              enableAutomaticChargeOff: data.enableAutomaticChargeOff,
              generateAnnualStatement: data.generateAnnualStatement,
              enablePersistentDebtCalculation: data.enablePersistentDebtCalculation,
            },
          },
        ],
      };
      await create(request);
    }
    refreshAccountTreatmentIndicators();
  }

  const formContext = useForm<AccountTreatmentIndicatorFormModel>();

  const id: InputProps<AccountTreatmentIndicatorFormModel> = {
    name: 'id',
    label: 'Id',
    defaultValue: accountTreatmentIndicator?.id,
  };

  const name: InputProps<AccountTreatmentIndicatorFormModel> = {
    name: 'name',
    label: 'Name',
    options: { required: 'Name is required' },
    defaultValue: accountTreatmentIndicator?.name,
  };

  const accountFlag: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'accountFlag',
    label: 'Account Flag',
    selectOptions: treatmentFlags,
    defaultValue: accountTreatmentIndicator?.accountFlag,
  };

  const acceptAuths: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'acceptAuths',
    label: 'AcceptAuths',
    options: { required: 'AcceptAuths is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.acceptAuths || 'true',
  };

  const acceptClearing: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'acceptClearing',
    label: 'AcceptClearing',
    options: { required: 'AcceptClearing is required' },
    selectOptions: toSelectOption(['true', 'false', 'report']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.acceptClearing || 'true',
  };

  const accrueInterest: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'accrueInterest',
    label: 'AccrueInterest',
    options: { required: 'AccrueInterest is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.accrueInterest || 'true',
  };

  const chargeInterest: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'chargeInterest',
    label: 'ChargeInterest',
    options: { required: 'ChargeInterest is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.chargeInterest || 'true',
  };

  const chargeFees: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'chargeFees',
    label: 'ChargeFees',
    options: { required: 'ChargeFees is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.chargeFees || 'true',
  };

  const generateStatements: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'generateStatements',
    label: 'GenerateStatements',
    options: { required: 'GenerateStatements is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.generateStatements || 'true',
  };

  const allowRegularPaymentCollection: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'allowRegularPaymentCollection',
    label: 'AllowRegularPaymentCollection',
    options: { required: 'AllowRegularPaymentCollection is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.allowRegularPaymentCollection || 'true',
  };

  const pauseBilling: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'pauseBilling',
    label: 'PauseBilling',
    options: { required: 'PauseBilling is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.pauseBilling || 'false',
  };

  const allowStatementDayChange: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'allowStatementDayChange',
    label: 'AllowStatementDayChange',
    options: { required: 'AllowStatementDayChange is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.allowStatementDayChange || 'true',
  };

  const enableNewDuesAndAging: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'enableNewDuesAndAging',
    label: 'EnableNewDuesAndAging',
    options: { required: 'EnableNewDuesAndAging is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.enableNewDuesAndAging || 'true',
  };

  const enablePaymentDueDateNosiaCheck: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'enablePaymentDueDateNosiaCheck',
    label: 'EnablePaymentDueDateNosiaCheck',
    options: { required: 'EnablePaymentDueDateNosiaCheck is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.enablePaymentDueDateNosiaCheck || 'true',
  };

  const enableRetrospectiveNosiaCheck: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'enableRetrospectiveNosiaCheck',
    label: 'EnableRetrospectiveNosiaCheck',
    options: { required: 'EnableRetrospectiveNosiaCheck is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.enableRetrospectiveNosiaCheck || 'true',
  };

  const enableAutomaticChargeOff: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'enableAutomaticChargeOff',
    label: 'EnableAutomaticChargeOff',
    options: { required: 'EnableAutomaticChargeOff is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.enableAutomaticChargeOff || 'true',
  };

  const generateAnnualStatement: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'generateAnnualStatement',
    label: 'GenerateAnnualStatement',
    options: { required: 'GenerateAnnualStatement is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.generateAnnualStatement || 'true',
  };

  const enablePersistentDebtCalculation: SelectProps<AccountTreatmentIndicatorFormModel> = {
    name: 'enablePersistentDebtCalculation',
    label: 'EnablePersistentDebtCalculation',
    options: { required: 'EnablePersistentDebtCalculation is required' },
    selectOptions: toSelectOption(['true', 'false']),
    defaultValue: accountTreatmentIndicator?.behaviourSettings?.enablePersistentDebtCalculation || 'true',
  };

  return (
    <ComponentErrorBoundary>
      <FormProvider {...formContext}>
        <FormContainer isSubmitting={isCreating || isUpdating} onSubmit={onSaveTreatment} disableButtons>
          {accountTreatmentIndicator && <Input {...id} disabled />}
          <Input {...name} disabled />
          <Select {...accountFlag} disabled />
          <div className={'ui card sixteen wide column'} style={{ marginTop: '0' }}>
            <div className="custom-card-header content" style={{ margin: '0', padding: '0', paddingBottom: '10px' }}>
              Behaviour Settings
            </div>
            {!accountTreatmentIndicator ? (
              <div className="content" style={{ margin: '0', paddingLeft: '0', paddingBottom: '0' }}>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...acceptAuths} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...acceptClearing} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...accrueInterest} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...chargeInterest} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...chargeFees} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...generateStatements} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...allowRegularPaymentCollection} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...pauseBilling} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...allowStatementDayChange} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...enableNewDuesAndAging} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...enablePaymentDueDateNosiaCheck} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...enableRetrospectiveNosiaCheck} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...enableAutomaticChargeOff} disabled />
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <Select {...generateAnnualStatement} disabled />
                </div>
                <div>
                  <Select {...enablePersistentDebtCalculation} disabled />
                </div>
              </div>
            ) : (
              <div className="content" style={{ margin: '0', paddingLeft: '0', paddingBottom: '0' }}>
                Please look at the product configuration for the behaviour settings
              </div>
            )}
          </div>
        </FormContainer>
      </FormProvider>
    </ComponentErrorBoundary>
  );
}
